import React, { useState, useEffect, useRef } from 'react';
import { Select, message } from 'antd';
import moment from 'moment';
import DataParser from '../../../utlits/Dataparser';
import TaskServices from '../../../Services/TaskServices';
import AssigneField from '../../Reuseable/AssigneField';
import LeadPendingField from '../../Reuseable/LeadPendingField';
import DateField from '../../Reuseable/DateField';

const { Option } = Select;

const InlineSubtaskRow = ({ parentTask, level = 0, taskOwner, taskPriority, taskStatus, companyId, COL, onCreated, onCancel, minHeight = '44px', padding = '4px 0' }) => {
    const [inlineTitle, setInlineTitle]       = useState('');
    const [inlinePriority, setInlinePriority] = useState(undefined);
    const [inlineAssignee, setInlineAssignee] = useState(() => String(DataParser.UserId() || ''));
    const [inlineDueDate, setInlineDueDate]   = useState(moment().format('YYYY-MM-DD'));
    const [inlineStatus, setInlineStatus]     = useState(() => {
        if (Array.isArray(taskStatus) && taskStatus.length) {
            return taskStatus.find(s => /^pending$/i.test(s)) || taskStatus[0];
        }
        return 'Pending';
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const hasSubmittedRef = useRef(false);
    const titleInputRef   = useRef(null);
    const rowRef          = useRef(null);
    const doCreateRef     = useRef(null);

    useEffect(() => { titleInputRef.current?.focus(); }, []);

    const doCreate = async () => {
        if (hasSubmittedRef.current || isSubmitting) return;
        if (!inlineTitle.trim() || !inlineAssignee || !inlineStatus) return;
        if (!inlinePriority) { message.warning('Please select a priority'); return; }
        hasSubmittedRef.current = true;
        setIsSubmitting(true);
        const isProjectRoot = parentTask.project_only || !parentTask.id;
        const body = {
            company_id: companyId,
            company_identifier: DataParser.CompanyIdentifier(),
            title: inlineTitle.trim(),
            assign_to: inlineAssignee,
            due_date: inlineDueDate ? moment(inlineDueDate).format('YYYY-MM-DD') : null,
            priority: inlinePriority,
            status: inlineStatus,
            project_id: parentTask.project_id || 0,
        };
        if (!isProjectRoot) {
            body.parent_id = parentTask.id;
        }
        try {
            const res = isProjectRoot 
                ? await TaskServices.createTask(body)
                : await TaskServices.createSubTask(parentTask.id, body);
            if (res.Good) {
                message.success(isProjectRoot ? 'Task created!' : 'Subtask created!');
                onCreated();
            } else {
                message.error(res.data || (isProjectRoot ? 'Failed to create task' : 'Failed to create subtask'));
                hasSubmittedRef.current = false;
                setIsSubmitting(false);
            }
        } catch {
            message.error(isProjectRoot ? 'Failed to create task' : 'Failed to create subtask');
            hasSubmittedRef.current = false;
            setIsSubmitting(false);
        }
    };

    doCreateRef.current = doCreate;

    useEffect(() => {
        const handleMouseDown = (e) => {
            if (hasSubmittedRef.current) return;
            if (rowRef.current && rowRef.current.contains(e.target)) return;
            if (e.target.closest('.ant-select-dropdown') || e.target.closest('.ant-picker-dropdown')) return;
            const title = titleInputRef.current?.value?.trim() ?? '';
            if (title) {
                doCreateRef.current();
            } else {
                onCancel();
            }
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const indent = level * 28;
    const selectedPriorityColor = taskPriority?.find(p => String(p.id) === String(inlinePriority))?.color;
    const selectedAssigneeUser  = inlineAssignee ? (taskOwner || []).find(u => String(u.id) === String(inlineAssignee)) : null;

    return (
        <div ref={rowRef} style={{ display: 'flex', width: '100%', alignItems: 'center', minHeight, borderBottom: '1px solid #f3f4f6', padding, background: '#f9fafb' }}>
        <style>{`
            .inline-subtask-title::placeholder { color: #9ca3af !important; font-weight: 400 !important; }
            .inline-subtask-title:focus, .inline-subtask-title:active, .inline-subtask-title:hover {
                border: none !important; outline: none !important; box-shadow: none !important;
            }
            .inline-priority-sel .ant-select-selector {
                border: none !important; box-shadow: none !important; outline: none !important;
                padding-left: 0 !important;
            }
            .inline-priority-sel.ant-select-focused .ant-select-selector,
            .inline-priority-sel .ant-select-selector:focus,
            .inline-priority-sel .ant-select-selector:active {
                border: none !important; box-shadow: none !important; outline: none !important;
            }
            .inline-priority-sel .ant-select-selector {
                display: flex !important; align-items: center !important; height: 100% !important;
            }
            .inline-priority-sel .ant-select-selection-placeholder {
                font-size: 13px !important; font-weight: 400 !important; color: #9ca3af !important;
                display: flex !important; align-items: center !important; line-height: normal !important; flex: 1 !important;
            }
            .inline-priority-sel .ant-select-selection-item {
                font-size: 13px !important; font-weight: 500 !important; color: #1d1d1d !important;
                display: flex !important; align-items: center !important; line-height: normal !important;
            }
            .inline-priority-sel .ant-select-arrow {
                width: 20px !important; height: 20px !important; margin-top: -10px !important; right: 8px !important;
            }
        `}</style>

            {/* Priority colour bar */}
            <div style={{ width: '4px', alignSelf: 'stretch', background: selectedPriorityColor || '#e5e7eb', flexShrink: 0, borderRadius: '0 4px 4px 0' }} />

            {/* Title */}
            <div style={{ flex: 1, minWidth: 0, paddingLeft: indent + 8, paddingRight: 8, overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                    ref={titleInputRef}
                    type="text"
                    value={inlineTitle}
                    onChange={e => setInlineTitle(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Escape') onCancel();
                        if (e.key === 'Enter' && !isSubmitting) doCreate();
                    }}
                    placeholder="Subtask title"
                    disabled={isSubmitting}
                    className="inline-subtask-title"
                    style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: '13px', fontWeight: '500', color: '#111827', paddingRight: '172px' }}
                />
                <span style={{ position: 'absolute', right: 10, fontSize: '10px', color: '#c0c4cc', whiteSpace: 'nowrap', userSelect: 'none', pointerEvents: 'none' }}>
                    Press Enter or click outside to save
                </span>
            </div>

            {/* Priority */}
            <div style={{ width: COL?.created || '160px', flexShrink: 0, overflow: 'hidden', padding: '0 8px' }}>
                <Select
                    className="inline-priority-sel"
                    value={inlinePriority || undefined}
                    onChange={val => setInlinePriority(val)}
                    placeholder="Priority"
                    style={{ width: '100%' }}
                    disabled={isSubmitting}
                    getPopupContainer={() => document.body}
                    bordered={false}
                    optionLabelProp="label"
                    suffixIcon={
                        <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false" style={{ fill: '#cccccc', display: 'inline-block', verticalAlign: 'middle' }}>
                            <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
                        </svg>
                    }
                >
                    {(taskPriority || []).map(p => (
                        <Option key={p.id} value={String(p.id)} label={p.name}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ backgroundColor: p.color, width: '8px', height: '8px', borderRadius: '50%', marginRight: '6px', display: 'inline-block', flexShrink: 0 }} />
                                {p.name}
                            </div>
                        </Option>
                    ))}
                </Select>
            </div>

            {/* Assigned To */}
            <div style={{ width: COL?.assigned || '150px', flexShrink: 0, overflow: 'hidden', padding: '0 8px' }}>
                <AssigneField
                    item={{ id: 0 }}
                    assigne={taskOwner}
                    Owner={selectedAssigneeUser || null}
                    Services={null}
                    localUpdate={(_, data) => {
                        if (data.asigned_to != null) setInlineAssignee(String(data.asigned_to));
                    }}
                    avatarSize="24px"
                    bgColor="#0DDFAB"
                    textColor="#1d1d1d"
                    styles={{ fontSize: '13px' }}
                    disabled={isSubmitting}
                />
            </div>

            {/* Due Date — capped by parent task due date */}
            <div style={{ width: COL?.date || '120px', flexShrink: 0, overflow: 'hidden', padding: '0 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i className="uil-calendar-alt" style={{ fontSize: '20px', color: '#0DDFAB', flexShrink: 0 }} />
                <DateField
                    item={{ id: 0 }}
                    defaultDate={inlineDueDate}
                    Services={(_, data) => { setInlineDueDate(data.duedate); return Promise.resolve({ Good: true }); }}
                    data={(newDate) => setInlineDueDate(newDate)}
                    styles={{ fontSize: '13px', color: '#1d1d1d', fontWeight: '500' }}
                    suffixIcon={null}
                    format="D, MMMM"
                    maxDate={parentTask?.due_date || null}
                    disabled={isSubmitting}
                />
            </div>

            {/* Status */}
            <div style={{ width: COL?.status || '140px', flexShrink: 0, overflow: 'hidden', padding: '0 6px' }}>
                <LeadPendingField
                    item={{ id: 0 }}
                    Status={taskStatus}
                    DefaultStatus={inlineStatus}
                    Services={(_, data) => Promise.resolve({ Good: true })}
                    data={(e) => setInlineStatus(e.target.value)}
                    disabled={isSubmitting}
                />
            </div>

            {/* Optional spacer (omitted when COL.comment is falsy or '0px') */}
            {COL?.comment && COL?.comment !== '0px' && (
                <div style={{ width: COL.comment, flexShrink: 0 }} />
            )}

            {/* Cancel / spinner */}
            <div style={{ width: COL?.project || '160px', flexShrink: 0, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                {isSubmitting
                    ? <div className="spinner-border spinner-border-sm" style={{ width: 16, height: 16, color: '#0DDFAB', borderRightColor: 'transparent' }} />
                    : (
                        <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 20, padding: '2px 4px', borderRadius: 4, lineHeight: 1 }}>
                            <i className="uil uil-times" />
                        </button>
                    )
                }
            </div>
        </div>
    );
};

export default InlineSubtaskRow;
